import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, CheckCircle, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { authService } from '@/features/auth/services/auth.service';

const resendActivationSchema = z.object({
  email: z.string().email(),
});

type ResendActivationForm = z.infer<typeof resendActivationSchema>;

export default function ResendActivationPage() {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResendActivationForm>({
    resolver: zodResolver(resendActivationSchema),
  });

  const onSubmit = async (data: ResendActivationForm) => {
    setIsSubmitting(true);
    try {
      await authService.resendActivationEmail(data.email);
      setIsSuccess(true);
    } catch {
      toast.error(t('activation.resendError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="w-full max-w-md text-center">
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
        </div>

        <h1 className="text-2xl font-bold mb-2">
          {t('activation.resendSuccessTitle')}
        </h1>

        <p className="text-muted-foreground mb-6">
          {t('activation.resendSuccessDescription')}
        </p>

        <Button asChild variant="outline" className="w-full">
          <Link to="/login">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('activation.backToLogin')}
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md">
      <div className="flex justify-center mb-6">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
          <Mail className="w-8 h-8 text-primary" />
        </div>
      </div>

      <h1 className="text-2xl font-bold text-center mb-2">
        {t('activation.resendTitle')}
      </h1>

      <p className="text-muted-foreground text-center mb-6">
        {t('activation.resendDescription')}
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <Input
            type="email"
            placeholder={t('auth.email')}
            {...register('email')}
            className={errors.email ? 'border-destructive' : ''}
          />
          {errors.email && (
            <p className="text-sm text-destructive mt-1">
              {t('validation.invalidEmail')}
            </p>
          )}
        </div>

        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? t('common.loading') : t('activation.resendButton')}
        </Button>

        <Button asChild variant="ghost" className="w-full">
          <Link to="/login">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('activation.backToLogin')}
          </Link>
        </Button>
      </form>
    </div>
  );
}
